import { createStore } from 'zustand/vanilla';
import type { ArrasteRemoto, EventoQuadroDto, PessoaResumoDto, TravaDto } from '@nexital/shared';

export type PosicaoRemota = { x: number; y: number; usuario: PessoaResumoDto; em: number };

/** Quem está no quadro e o que está fazendo agora (ADR-0008, RQ-COL-002 a RQ-COL-004). Nada disso é gravado. */
export type EstadoColaboracao = {
  online: boolean;
  presenca: PessoaResumoDto[];
  /** Por id do nó: posição enquanto um colega arrasta. */
  arrastes: Record<string, PosicaoRemota>;
  /** Por id do nó: quem tem a trava. */
  travas: Record<string, PessoaResumoDto>;
  /** Último evento recebido de outra aba (a licitação aberta relê quando é dela). */
  ultimoEvento: EventoQuadroDto | null;
  definirOnline(online: boolean): void;
  definirPresenca(pessoas: PessoaResumoDto[]): void;
  receberArraste(arraste: ArrasteRemoto): void;
  limparArrastesDe(usuarioUuid: string): void;
  definirTravas(travas: TravaDto[]): void;
  receberEvento(evento: EventoQuadroDto): void;
  /** Arraste sem novidade há mais que `idadeMs` (o colega soltou sem gravar ou caiu): o nó volta ao lugar. */
  limparArrastesParados(agora: number, idadeMs: number): void;
};

const posicoes = ({ usuario, itens }: ArrasteRemoto) => {
  const em = Date.now();
  return Object.fromEntries(itens.map((i) => [i.id, { x: i.x, y: i.y, usuario, em }]));
};

const semArrastesDe = (arrastes: Record<string, PosicaoRemota>, usuarioUuid: string) =>
  Object.fromEntries(Object.entries(arrastes).filter(([, a]) => a.usuario.uuid !== usuarioUuid));

/** Sem mudança (mesmo objeto) quando nada está parado, para não redesenhar o quadro a cada segundo. */
function semParados(s: EstadoColaboracao, limite: number): Partial<EstadoColaboracao> {
  const vivos = Object.entries(s.arrastes).filter(([, a]) => a.em > limite);
  return vivos.length === Object.keys(s.arrastes).length ? s : { arrastes: Object.fromEntries(vivos) };
}

export function criarStoreColaboracao() {
  return createStore<EstadoColaboracao>()((setState) => ({
    online: false,
    presenca: [],
    arrastes: {},
    travas: {},
    ultimoEvento: null,
    definirOnline: (online) => setState(online ? { online } : { online, arrastes: {}, travas: {} }),
    definirPresenca: (presenca) => setState({ presenca }),
    receberArraste: (arraste) => setState((s) => ({ arrastes: { ...s.arrastes, ...posicoes(arraste) } })),
    limparArrastesDe: (usuarioUuid) => setState((s) => ({ arrastes: semArrastesDe(s.arrastes, usuarioUuid) })),
    definirTravas: (travas) => setState({ travas: Object.fromEntries(travas.map((t) => [t.chave, t.usuario])) }),
    receberEvento: (ultimoEvento) => setState({ ultimoEvento }),
    limparArrastesParados: (agora, idadeMs) => setState((s) => semParados(s, agora - idadeMs)),
  }));
}

export type StoreColaboracao = ReturnType<typeof criarStoreColaboracao>;
